import React, { useRef } from "react";
import { Col } from "react-flexbox-grid";
import { useTranslation } from "react-i18next";

import { convertToSentenceCase, extractPropsForWineCard } from "../../../helpers/tools";
import urlPatterns from "../../../urls";
import { I18N_NAMESPACES } from "../../../helpers/constants";

import Button from "../../atoms/Button";
import Tooltip from "../../atoms/Tooltip";
import WineCard from "../../molecules/WineCard";
import InfiniteScrollContainer from "../../molecules/InfiniteScrollContainer";

import { AllWinesByWineListWithCount_allWinesByWineList } from "../../../types/__generated__/AllWinesByWineListWithCount";
import { WineList_wineList } from "../../../types/__generated__/WineList";
import { MemberShoppingCart } from "../../../types/__generated__/MemberShoppingCart";
import { Credit } from "../../../types/__generated__/Credit";
import ProductCardLoading from "../../molecules/ProductCardLoading";
import {
  StyledTitle,
  StyledLink,
  ResultsFound,
  RowComponent,
  ColComponent,
  NoResultsContainer,
  NoResultsMessage,
} from "./style";

interface TitleComponentProps {
  nameFilter?: string | null;
  numberOfFilterSelected: number;
  wineList?: WineList_wineList | null;
  resultCount?: number | null;
}

const TitleComponent = ({
  nameFilter,
  numberOfFilterSelected,
  resultCount,
  wineList,
}: TitleComponentProps) => {
  // Add translations that are nested within the wine details tray,
  // to not see a loading spinner if the tray is opened.
  const { t } = useTranslation([
    I18N_NAMESPACES.WINE_PAGES,
    I18N_NAMESPACES.COMMON,
    I18N_NAMESPACES.MY_BADGES,
    I18N_NAMESPACES.MY_BOX,
    I18N_NAMESPACES.MY_CELLAR,
    I18N_NAMESPACES.MY_DASHBOARD,
  ]);

  if (nameFilter) {
    return (
      <ResultsFound>
        {t("winePages:searchResult", {
          count: resultCount ?? 0,
          context: resultCount != null ? String(resultCount) : undefined,
        })}
        {t("winePages:forWord", {
          word: nameFilter,
          interpolation: { escapeValue: false },
        })}
      </ResultsFound>
    );
  }

  if (numberOfFilterSelected > 0) {
    return (
      <ResultsFound>
        {t("winePages:filteredResult", {
          count: resultCount ?? 0,
          context: resultCount != null ? String(resultCount) : undefined,
        })}
      </ResultsFound>
    );
  }

  return (
    <StyledTitle>
      {convertToSentenceCase(wineList?.name || "")}
      {wineList?.description && <Tooltip tootipTitle={wineList.description} />}
    </StyledTitle>
  );
};

export interface AllWinesOfWineListProps {
  wines?: (AllWinesByWineListWithCount_allWinesByWineList | null)[] | null;
  wineList?: WineList_wineList | null;
  wineCount?: number | null;
  isLoading: boolean;
  nameFilter?: string;
  numberOfFilterSelected: number;
  memberShoppingCart?: MemberShoppingCart;
  onToggleWineDetailsTray: (selectedWineSlug: string) => void;
  onToggleBookmarksTray: (wineId: number) => void;
  onToggleAddBottleTray: (() => void) | null;
  onSetHeaderDisplay: (isShown: boolean) => void;
  onFetchMoreRequested: () => void;
  addBottleToCredit?: Credit;
}

const AllWinesOfWineList = ({
  wines,
  wineList,
  wineCount,
  isLoading,
  nameFilter,
  numberOfFilterSelected,
  memberShoppingCart,
  onToggleWineDetailsTray,
  onToggleBookmarksTray,
  onToggleAddBottleTray,
  onSetHeaderDisplay,
  onFetchMoreRequested,
  addBottleToCredit,
}: AllWinesOfWineListProps) => {
  const { t } = useTranslation([I18N_NAMESPACES.WINE_PAGES, I18N_NAMESPACES.COMMON]);
  const showMoreButtonRef = useRef(null);
  // If the number of wines currently fetched is strictly lower than the number of wines matching the filters
  // show a button to fetch more wines
  const hasMoreToFetch = (wines?.length ?? 0) < (wineCount ?? 0);

  return (
    <>
      <TitleComponent
        nameFilter={nameFilter}
        numberOfFilterSelected={numberOfFilterSelected}
        wineList={wineList}
        resultCount={wineCount}
      />
      <RowComponent>
        {!wines?.length && !isLoading && (
          <Col md={12}>
            <NoResultsContainer>
              <NoResultsMessage>{t("winePages:noWay")}</NoResultsMessage>
              <NoResultsMessage>
                <StyledLink to={urlPatterns.SPECIAL_PACKS}>{t("winePages:headHere")}</StyledLink>{" "}
                {t("winePages:ifYou")}
              </NoResultsMessage>
            </NoResultsContainer>
          </Col>
        )}
        {wines?.map((wine) => (
          <ColComponent xl={3} lg={4} md={4} sm={6} xs={6} key={wine?.id}>
            <WineCard
              // @ts-ignore
              memberShoppingCartQuery={memberShoppingCart || {}}
              wine={wine}
              toggleWineDetailsTray={onToggleWineDetailsTray}
              toggleBookmarksTray={onToggleBookmarksTray}
              toggleAddBottleTray={onToggleAddBottleTray}
              setHeaderDisplay={onSetHeaderDisplay}
              {...extractPropsForWineCard(wine ?? {})}
              addBottleToCredit={addBottleToCredit}
            />
          </ColComponent>
        ))}
        {isLoading &&
          [1, 2, 3, 4, 5, 6, 7, 8].map((idx) => (
            <ColComponent xl={3} lg={4} md={4} sm={6} xs={6} key={idx}>
              <ProductCardLoading />
            </ColComponent>
          ))}
      </RowComponent>
      <InfiniteScrollContainer
        isLoading={isLoading}
        hasMore={hasMoreToFetch}
        fetchMore={onFetchMoreRequested}
        showMoreButtonRef={showMoreButtonRef}
      >
        <RowComponent>
          <Col md={12}>
            {!isLoading && hasMoreToFetch && (
              <Button
                // @ts-ignore
                ref={showMoreButtonRef}
                outline
                full
                fullMobile
                onClick={onFetchMoreRequested}
              >
                {t("common:showMore")}
              </Button>
            )}
          </Col>
        </RowComponent>
      </InfiniteScrollContainer>
    </>
  );
};

export default AllWinesOfWineList;
